import React, { useCallback, useEffect, useState, useRef, lazy, Suspense } from 'react'
import debounce from 'lodash/debounce'
import ComboBox from '@react/react-spectrum/ComboBox'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { selectedIcons, allRegularIcons, allSolidIcons } from 'config/fontAwesome'
import {
  selectedIcons as mobileV2SelectedIcons,
  selectedOutlineIcons as mobileV2SelectedOutlineIcons,
} from 'config/materialIcons'

import './IconPicker.scss'

const MobileV2Icon = lazy(() => import('./MobileV2Icon'))

const allIcons = allRegularIcons.concat(allSolidIcons)

const getIconOption = (name) => ({
  label: name,
  value: name,
  icon: (
    <span className='iconPicker-listIcon'>
      <FontAwesomeIcon icon={name} size='sm' />
    </span>
  ),
})

const getMobilev2IconOption = (name) => ({
  label: name,
  value: name,
  icon: (
    <span className='iconPicker-listIcon'>
      <Suspense fallback={<FontAwesomeIcon icon='spinner' spin size='sm' />}>
        <MobileV2Icon name={name} className='iconPicker-listIcon--mobilev2' />
      </Suspense>
    </span>
  ),
})

const getDefaultOptions = (value, iconSet) => {
  let defaultOptions = (iconSet || selectedIcons).map(getIconOption)
  if (!defaultOptions.find((opt) => opt.value === value)) {
    defaultOptions.push(getIconOption(value))
  }

  return defaultOptions
}

const getMobileV2DefaultOptions = (value, isOutline = false) => {
  const mappedIconOptions = (isOutline ? mobileV2SelectedOutlineIcons : mobileV2SelectedIcons).map(
    getMobilev2IconOption
  )
  if (!mappedIconOptions.find((opt) => opt.value === value)) {
    mappedIconOptions.push(getMobilev2IconOption(value))
  }

  return mappedIconOptions
}

const IconPicker = ({ name, value, disabled, onChange, iconSet, isMobileV2 = false, isOutlineIcon = false }) => {
  const comboBoxRef = useRef()
  const [input, setInput] = useState(value)
  const [isInvalid, setIsInvalid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState(!isMobileV2 ? getDefaultOptions(value, iconSet) : [])
  const [showMenu, setShowMenu] = useState(false)

  useEffect(() => {
    if (isMobileV2) {
      setOptions(getMobileV2DefaultOptions(value, isOutlineIcon))
    }
    // This is a hack based on how this component is written for now - should be changed in the future (probably with Argon rewrite)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOptions, isMobileV2, isOutlineIcon])

  const toggleMenu = () => {
    setShowMenu((prevState) => {
      const refInputElement = comboBoxRef.current?.textfield?.props?.id
      if (!refInputElement || document.getElementById(refInputElement) === document.activeElement) {
        return !prevState
      }
      return false
    })
  }

  const validationState = isInvalid ? 'invalid' : null

  const validateInput = useCallback(
    debounce(async () => {
      let allIconsSet
      if (isMobileV2) {
        const { default: mobileIcons } = await import('./mobilev2')
        allIconsSet = mobileIcons
      } else {
        allIconsSet = iconSet || allIcons
      }
      const searchResults = allIconsSet.filter((name) => name.includes(input)).slice(0, 50)
      setIsInvalid(!searchResults?.length)
      if (isMobileV2) {
        setOptions(searchResults.map(getMobilev2IconOption))
      } else {
        setOptions(searchResults.map(getIconOption))
      }
      if (searchResults.includes(input)) {
        setIsLoading(false)
        setShowMenu(false)
        onChange(input)
      } else {
        setShowMenu(true)
      }
    }, 300),
    [iconSet, input, setShowMenu, isMobileV2]
  )

  const onSelect = (i) => {
    if (!disabled) {
      onChange(i.label)
      setInput(i.label)
    }
  }

  useEffect(() => {
    setInput(value)
  }, [value])

  useEffect(() => {
    if (!disabled) {
      setIsLoading(true)
      validateInput(input)
    }
    // eslint-disable-next-line
  }, [input])

  useEffect(() => {
    if (!disabled) {
      setIsLoading(false)
      return validateInput.cancel()
    }
  }, [])

  return (
    <div className='iconPicker'>
      <ComboBox
        ref={comboBoxRef}
        name={name}
        value={input}
        className='iconPicker-select'
        placeholder='Choose an icon'
        options={options}
        showMenu={!disabled && showMenu}
        onSelect={onSelect}
        onChange={setInput}
        disabled={disabled}
        validationState={validationState}
        onMenuToggle={toggleMenu}
      ></ComboBox>
      <ChosenIcon isLoading={isLoading || isInvalid} isMobileV2={isMobileV2} name={input} />
    </div>
  )
}

function ChosenIcon({ isLoading, isMobileV2, name }) {
  if (isLoading) {
    return <FontAwesomeIcon className='iconPicker-current' icon='spinner' spin size='sm' />
  }

  if (!isMobileV2) {
    return <FontAwesomeIcon className='iconPicker-current' icon={name} size='sm' />
  }

  return (
    <Suspense fallback={<FontAwesomeIcon icon='spinner' spin size='sm' className='iconPicker-current' />}>
      <MobileV2Icon name={name} className='iconPicker-current iconPicker-current--mobilev2' />
    </Suspense>
  )
}

export default IconPicker
